import moment from 'moment'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { FILTER_DEFAULT_SKIP, FILTER_DEFAULT_TAKE } from '@apps-common/config/filter'
import { UnitTaskSort } from '@apps-common/config/unit-task'
import { IUnitTaskGroupsSearchParamsModel } from '@apps-common/types/portal/unit-tasks'

export const getUnitTaskGroupFilterDefaultStartDateStr = () => '2000-01-01T00:00:00'
export const getUnitTaskGroupFilterDefaultEndDateStr = () => moment().add(1, 'day').toISOString()

export const DEFAULT_UNIT_TASK_GROUPS_FILTER_STORE_PROPS: IUnitTaskGroupsFilterProps = {
  groupName: '',
  status: '',
  taskType: '',
  startDate: undefined,
  endDate: undefined,
  skip: FILTER_DEFAULT_SKIP,
  take: FILTER_DEFAULT_TAKE,
  orderBy: UnitTaskSort.dateAdded
}

export interface IUnitTaskGroupsFilterProps {
  groupName: string
  status: string
  taskType: string
  startDate?: string
  endDate?: string
  skip: number
  take: number
  orderBy: string
}

export interface IUnitTaskGroupsFilterState extends IUnitTaskGroupsFilterProps {
  setGroupName: (value: string) => void
  setStatus: (value: string) => void
  setTaskType: (value: string) => void
  setStartDate: (value: string) => void
  setEndDate: (value: string) => void
  setSkip: (value: number) => void
  setTake: (value: number) => void
  setOrderBy: (orderBy: string) => void
  getSearchParams: () => IUnitTaskGroupsSearchParamsModel
  getFilteredByKeywords: () => string[]
  clear: () => void
}

export const useUnitTaskGroupsFilterStore = create<IUnitTaskGroupsFilterState, [['zustand/persist', unknown]]>(
  persist(
    (set, get) => ({
      ...DEFAULT_UNIT_TASK_GROUPS_FILTER_STORE_PROPS,
      setGroupName: (value: string) => {
        set(() => ({ groupName: value, skip: FILTER_DEFAULT_SKIP }))
      },
      setStatus: (value: string) => {
        const { status } = get()

        if (status !== value) {
          set(() => ({ status: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setTaskType: (value: string) => {
        set(() => ({ taskType: value, skip: FILTER_DEFAULT_SKIP }))
      },
      setStartDate: (value: string) => {
        const { startDate } = get()

        if (startDate !== value) {
          set(() => ({ startDate: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setEndDate: (value: string) => {
        const { endDate } = get()

        if (endDate !== value) {
          set(() => ({ endDate: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      setSkip: (value: number) => set(() => ({ skip: value })),
      setTake: (value: number) => set(() => ({ take: value })),
      setOrderBy: (value: string) => {
        const { orderBy } = get()

        if (orderBy !== value) {
          set(() => ({ orderBy: value, skip: FILTER_DEFAULT_SKIP }))
        }
      },
      getSearchParams: () => {
        const { groupName, status, taskType, startDate, endDate, skip, take, orderBy } = get()

        return {
          groupName,
          status,
          taskType,
          startDate,
          endDate,
          skip,
          take,
          orderBy
        }
      },
      getFilteredByKeywords: () => {
        const { groupName, status, taskType, startDate, endDate, skip, take, orderBy } = get()

        const keywords = []

        if (groupName && groupName !== '') {
          keywords.push('Name')
        }

        if (status && status !== '') {
          keywords.push('Status')
        }

        if (taskType && taskType !== '') {
          keywords.push('Type')
        }

        if (!!startDate || !!endDate) {
          keywords.push('Date')
        }

        if (orderBy && orderBy !== UnitTaskSort.dateAdded) {
          keywords.push('Order')
        }

        return keywords
      },
      clear: () => {
        set(() => DEFAULT_UNIT_TASK_GROUPS_FILTER_STORE_PROPS)
      }
    }),
    {
      name: 'unit-task-groups-filter-store'
    }
  )
)
